import { Env } from '@env';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import {
  APIProvider,
  ColorScheme,
  Map,
  useMap,
} from '@vis.gl/react-google-maps';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import type { CarMarkerProps } from '../search/car-marker';
import CarMarker from '../search/car-marker';
import { Circle } from './circle';
import { ZoomButtons } from './zoom-buttons';

export interface MapControllerProps {
  defaultCenter: {
    latitude: number;
    longitude: number;
  };
  polylines: {
    coordinates: [
      {
        latitude: number;
        longitude: number;
      }
    ];
    strokeColor: string;
    strokeWidth: number;
  }[];
  zoomEnabled?: boolean;
}

const MapController = ({
  defaultCenter,
  polylines,
  zoomEnabled = true,
  zoom,
}: MapControllerProps) => {
  const map = useMap('map');
  const lastCenter = useRef<{ map: google.maps.Map | null; key: string }>({
    map: null,
    key: '',
  });
  // const maps = useMapsLibrary('maps');

  useEffect(() => {
    if (!map || !defaultCenter) return;

    const key = `${defaultCenter.latitude},${defaultCenter.longitude}`;
    const sameCenter = lastCenter.current.key === key;
    const mapRecreated =
      lastCenter.current.map !== null && lastCenter.current.map !== map;

    lastCenter.current = { map, key };

    if (mapRecreated && sameCenter) return;

    map.setCenter({
      lat: defaultCenter.latitude,
      lng: defaultCenter.longitude,
    });
  }, [map, defaultCenter]);

  useEffect(() => {
    if (map && zoom) {
      map.setZoom(zoom);
    }
  }, [zoom]);

  // useEffect(() => {
  //   if (!map || !maps) return;

  //   polylines.forEach((polyline) => {
  //     const pl = new maps.Polyline({
  //       path: polyline.coordinates.map((c) => ({
  //         lat: c.latitude,
  //         lng: c.longitude,
  //       })),
  //       geodesic: true,
  //       strokeColor: polyline.strokeColor,
  //       strokeWeight: polyline.strokeWidth,
  //     });

  //     pl.setMap(map);
  //   });
  // }, [map, maps, polylines]);

  if (!map || !zoomEnabled) return;

  return null;

  return (
    <View
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <ZoomButtons
        onIncrease={() => {
          const zoom = map.getZoom();

          if (!zoom) return;

          map.setZoom(zoom + 1);
        }}
        onDecrease={() => {
          const zoom = map.getZoom();

          if (!zoom) return;

          map.setZoom(zoom - 1);
        }}
      />
    </View>
  );
};

const MarkersContainer = forwardRef<
  any,
  { markers: CarMarkerProps[]; fitBounds?: boolean }
>(({ markers, fitBounds = false }, ref) => {
  const map = useMap('map');

  useImperativeHandle(ref, () => ({
    getBounds: () => {
      const bounds = map?.getBounds();

      if (!bounds) {
        return null;
      }

      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      return {
        northEast: { latitude: northEast.lat(), longitude: northEast.lng() },
        southWest: { latitude: southWest.lat(), longitude: southWest.lng() },
      };
    },
  }));

  useEffect(() => {
    if (map && markers && fitBounds) {
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker) => {
        bounds.extend({ lat: marker.latitude, lng: marker.longitude });
      });

      map.fitBounds(bounds, 0);
    }
  }, [map, markers]);

  return markers.map((marker, index) => (
    <CarMarker
      key={index}
      latitude={marker.latitude}
      longitude={marker.longitude}
      text={marker.text}
      icon={marker.icon}
      onPress={marker.onPress}
      active={marker.active}
      infoWindow={marker.infoWindow}
    />
  ));
});

interface CircleProps {
  center: { latitude: number; longitude: number };
  radius: number;
  strokeColor: string;
  strokeWidth: number;
  fillColor: string;
}

interface MapViewProps {
  polylines?: MapControllerProps.polylines;
  markers?: CarMarkerProps[];
  onDrag?: () => void;
  onPress?: () => void;
  defaultCenter?: { latitude: number; longitude: number };
  containerStyle?: any;
  scrollEnabled?: boolean;
  zoomEnabled?: boolean;
  fitBounds?: boolean;
  zoom?: number;
  altitude?: number;
  onLoad?: () => void;
  circles?: CircleProps[];
}

export const MapView = forwardRef<any, MapViewProps>(
  (
    {
      polylines,
      markers,
      onDrag,
      onPress,
      defaultCenter,
      containerStyle,
      scrollEnabled = true,
      zoomEnabled = true,
      fitBounds = false,
      zoom = 8.5,
      onLoad,
      circles,
    },
    ref
  ) => {
    const markersRef = useRef<any>(null);
    const cameraRef = useRef<{
      center: { lat: number; lng: number };
      zoom: number;
    } | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    useStyles();

    const colorScheme =
      UnistylesRuntime.themeName === 'dark'
        ? ColorScheme.DARK
        : ColorScheme.LIGHT;

    useImperativeHandle(ref, () => ({
      getBounds: () => {
        console.log('web Function triggered from parent component');
        return markersRef.current?.getBounds();
      },
    }));

    return (
      <APIProvider
        apiKey={Env.GOOGLE_WEB_API_KEY}
        version="beta"
        libraries={['places']}
      >
        <Map
          id="map"
          mapId="307bac7ba4a90cd6"
          colorScheme={colorScheme}
          style={{ flexGrow: 1 }}
          defaultCenter={
            cameraRef.current?.center ??
            ((defaultCenter && {
              lat: defaultCenter.latitude,
              lng: defaultCenter.longitude,
            }) || { lat: 37.78825, lng: -122.4324 })
          }
          defaultZoom={cameraRef.current?.zoom ?? zoom}
          onCameraChanged={(event) => {
            cameraRef.current = {
              center: event.detail.center,
              zoom: event.detail.zoom,
            };
          }}
          gestureHandling={scrollEnabled ? 'greedy' : 'none'}
          disableDefaultUI={true}
          onDragend={() => {
            onDrag?.();
          }}
          // onBoundsChanged={handleCameraChange}
          onClick={(e: MapMouseEvent) => {
            onPress?.();
          }}
          zoomControl={zoomEnabled}
          scrollwheel={scrollEnabled}
          clickableIcons={false}
          onIdle={() => {
            if (!mapLoaded) {
              setMapLoaded(true);
              onLoad?.();
            }
          }}
        >
          {markers ? (
            <MarkersContainer
              ref={markersRef}
              markers={markers}
              fitBounds={fitBounds}
            />
          ) : null}

          {circles
            ? circles.map((circle, index) => (
                <Circle
                  key={index}
                  center={{
                    lat: circle.center.latitude,
                    lng: circle.center.longitude,
                  }}
                  radius={circle.radius}
                  strokeColor={circle.strokeColor}
                  strokeWeight={circle.strokeWidth}
                  strokeOpacity={1}
                  fillColor={circle.fillColor}
                  fillOpacity={1}
                />
              ))
            : null}
        </Map>

        <MapController
          defaultCenter={defaultCenter}
          polylines={polylines || []}
          zoomEnabled={zoomEnabled}
          zoom={zoom}
        />
      </APIProvider>
    );
  }
);
